import type {EventUserContactDetails} from "~/types/Events";

export const useBadgeStore = defineStore('BadgeStore', () => {
  const {$api} = useNuxtApp();
  const {currentUserId} = useWadAuth();

  // The persisted badge belongs to one user. If a different account logs in on the same
  // device we must never surface the previous user's badge, so the cache is tagged with the
  // owning user id and discarded on mismatch (logout also clears it explicitly).
  const cachedUserId = ref<number | null>(null);
  // Badge/contact details are per-event (the QR code is event-scoped), so cache by eventId.
  const cachedDetails = ref<Record<number, EventUserContactDetails>>({});

  const badgeFor = (eventId: number): EventUserContactDetails | null => {
    if (cachedUserId.value !== (currentUserId.value ?? null)) {
      return null;
    }
    return cachedDetails.value[eventId] ?? null;
  };

  const cacheDetails = (eventId: number, details: EventUserContactDetails) => {
    const userId = currentUserId.value ?? null;
    // A new owner invalidates everything cached for the previous one.
    if (cachedUserId.value !== userId) {
      cachedUserId.value = userId;
      cachedDetails.value = {};
    }
    cachedDetails.value = {...cachedDetails.value, [eventId]: details};
  };

  // Fetch the badge, persisting the latest response so it can be rendered offline later. When
  // the request fails, fall back to the last successfully cached badge for this user+event;
  // only surface the offline error when there's nothing cached to show.
  //
  // Network-first by design: online always returns (and re-caches) fresh data; the persisted
  // cache is only an offline fallback. /contact-details is intentionally NOT in the service
  // worker's event-api-cache, so this is the single offline source. Note there's no fast
  // timeout here (unlike the SW's 3s NetworkFirst) — a very slow online connection waits on
  // the browser's default fetch timeout before resolving. Acceptable for now; if flaky venue
  // WiFi becomes an issue, race the fetch against a short timeout and serve cache on timeout.
  const loadContactDetails = async (eventId: number): Promise<EventUserContactDetails> => {
    try {
      const details = await $api.v2.getContactDetails(eventId) as EventUserContactDetails;
      cacheDetails(eventId, details);
      return details;
    } catch (error: any) {
      const cached = badgeFor(eventId);
      if (cached) {
        return cached;
      }
      throw createDataLoadError(error, 'Failed to load badge details');
    }
  };

  const clear = () => {
    cachedUserId.value = null;
    cachedDetails.value = {};
  };

  return {
    // cachedUserId/cachedDetails must be returned so they're part of $state and get persisted.
    cachedUserId,
    cachedDetails,
    badgeFor,
    cacheDetails,
    loadContactDetails,
    clear,
  };
}, {
  persist: {
    pick: ['cachedUserId', 'cachedDetails'],
    // Contact details carry PII and exceed the ~4KB cookie limit; this is client-only data
    // that never belongs in a request header, so persist it to localStorage (matching
    // OfflineLeadQueueStore) rather than the module's default cookie storage.
    storage: piniaPluginPersistedstate.localStorage(),
  },
});
